import { useState, useEffect } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { Confirm } from '../components/Confirm';
import { api, extractErrorMessage, getAccessToken } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useXlsxExport } from '../hooks/useXlsxExport';
import { Button } from '../components/Button';

interface Marca {
  nombre: string;
  proveedorId?: number;
  proveedor?: { id: number; nombre: string };
  descuento?: string | number;
}

interface ProveedorOption {
  id: number;
  nombre: string;
}

export function MarcasPage() {
  // GET /marcas devuelve un array plano; se acepta también { data } por compatibilidad.
  const { data: response, loading, refetch } = useApi<Marca[] | { data: Marca[]; total: number }>('/api/v3/marcas');
  const data: Marca[] = Array.isArray(response) ? response : response?.data ?? [];
  const [filters, setFilters] = useState<Record<string, string>>({});

  const filtered = data.filter((row) =>
    Object.entries(filters).every(([key, val]) => {
      if (!val) return true;
      if (key === 'proveedor') {
        return (row.proveedor?.nombre ?? '').toLowerCase().includes(val.toLowerCase());
      }
      return String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase());
    }),
  );

  // Proveedores for select
  const { data: provResponse } = useApi<ProveedorOption[] | { data: ProveedorOption[]; total: number }>('/api/v3/proveedores?limit=100');
  const proveedores = Array.isArray(provResponse) ? provResponse : (provResponse as any)?.data ?? [];

  const [open, setOpen] = useState(false);
  const [editingNombre, setEditingNombre] = useState<string | null>(null);
  const [form, setForm] = useState({ nombre: '', proveedorId: '', descuento: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Marca | null>(null);
  const [nombreError, setNombreError] = useState('');
  const [plantillaOpen, setPlantillaOpen] = useState(false);
  const toast = useToast();
  const { can } = useAuth();
  const { exportXlsx } = useXlsxExport();

  useEffect(() => {
    if (!plantillaOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlantillaOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [plantillaOpen]);

  // ── Modal helpers ──────────────────────────────────────────
  const cerrarForm = () => { setOpen(false); setEditingNombre(null); setForm({ nombre: '', proveedorId: '', descuento: '' }); };
  const cierreForm = useCierreConfirmado(useHayCambios(form, open), cerrarForm);

  const openCreate = () => {
    setEditingNombre(null);
    setForm({ nombre: '', proveedorId: '', descuento: '' });
    setNombreError('');
    setOpen(true);
  };

  const openEdit = (marca: Marca) => {
    if (!can('catalogo', 'editar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setEditingNombre(marca.nombre);
    setForm({ nombre: marca.nombre, proveedorId: marca.proveedorId ? String(marca.proveedorId) : '', descuento: marca.descuento != null ? String(Number(marca.descuento)) : '' });
    setNombreError('');
    setOpen(true);
  };

  const validateForm = (): string | null => {
    if (!form.nombre.trim()) return 'El nombre es obligatorio';
    if (form.descuento !== '') {
      const d = Number(form.descuento);
      if (!Number.isFinite(d) || d < 0 || d > 100) return 'El descuento debe estar entre 0 y 100';
    }
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { setNombreError(err); toast.addToast(err, 'error'); return; }
    setNombreError('');
    setSubmitting(true);
    try {
      const descuento = form.descuento !== '' ? Number(form.descuento) : 0;
      if (editingNombre && editingNombre === form.nombre.trim()) {
        // Mismo nombre: PATCH permite también quitar el proveedor.
        await api.patch(`/api/v3/marcas/${encodeURIComponent(editingNombre)}`, {
          proveedorId: form.proveedorId ? parseInt(form.proveedorId) : null,
          descuento,
        });
      } else {
        if (editingNombre) {
          // Nombre changed — delete old, create new
          await api.delete(`/api/v3/marcas/${encodeURIComponent(editingNombre)}`);
        }
        await api.post('/api/v3/marcas', {
          nombre: form.nombre.trim(),
          proveedorId: form.proveedorId ? parseInt(form.proveedorId) : undefined,
          descuento,
        });
      }
      toast.addToast(editingNombre ? 'Marca actualizada' : 'Marca creada', 'success');
      cerrarForm();
      refetch();
    } catch (e: any) {
      if (e?.status === 409) {
        toast.addToast('La marca ya existe con ese nombre', 'error');
      } else {
        toast.addToast(extractErrorMessage(e), 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (!can('catalogo', 'eliminar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setSubmitting(true);
    try {
      await api.delete(`/api/v3/marcas/${encodeURIComponent(deleteTarget.nombre)}`);
      toast.addToast(`"${deleteTarget.nombre}" eliminada`, 'success');
      setConfirmDelete(false);
      setDeleteTarget(null);
      refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    finally { setSubmitting(false); }
  };

  // ── Import / Export ────────────────────────────────────────

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await api.upload<{ creados?: number; actualizados?: number; fallos?: number; errores?: any[] }>(
        '/api/v3/marcas/importar',
        file,
      );
      const msgs: string[] = [];
      if (r.creados) msgs.push(`${r.creados} creadas`);
      if (r.actualizados) msgs.push(`${r.actualizados} actualizadas`);
      if (r.fallos) msgs.push(`${r.fallos} fallos`);
      toast.addToast(`Importación: ${msgs.join(', ')}`, r.fallos ? 'info' : 'success');
      refetch();
    } catch (e) { toast.addToast(extractErrorMessage(e), 'error'); }
    e.target.value = '';
  };

  const downloadTemplate = async (format: 'csv' | 'xlsx' = 'csv') => {
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const url = `/api/v3/marcas/plantilla${format === 'xlsx' ? '?format=xlsx' : ''}`;
      const res = await fetch(url, { headers, credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || err?.message || 'Error al descargar plantilla');
      }
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = format === 'xlsx'
        ? 'plantilla_marcas_stockearte.xlsx'
        : 'plantilla_marcas_stockearte.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      toast.addToast('Plantilla descargada', 'success');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  const handleXlsxExport = async () => {
    const columns = [
      { key: 'nombre', header: 'Nombre' },
      { key: 'proveedor', header: 'Proveedor' },
      { key: 'descuento', header: 'Desc. Marca %' },
    ];
    const rows = filtered.map((m) => ({
      nombre: m.nombre,
      proveedor: m.proveedor?.nombre ?? '—',
      descuento: Number(m.descuento ?? 0),
    }));
    try {
      await exportXlsx(columns, rows, 'Marcas');
      toast.addToast('Exportado a XLSX', 'success');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    }
  };

  // ── Columns ────────────────────────────────────────────────

  const columns = [
    {
      key: 'nombre',
      header: 'Nombre',
      help: 'Nombre de la marca (único)',
    },
    {
      key: 'proveedor',
      header: 'Proveedor',
      render: (r: Marca) => (
        <span className="text-slate-600">
          {r.proveedor?.nombre ?? <span className="text-slate-400 italic">Sin proveedor</span>}
        </span>
      ),
      help: 'Proveedor asociado a esta marca',
    },
    {
      key: 'descuento',
      header: 'Desc. Marca %',
      render: (r: Marca) => <span className="text-slate-600">{Number(r.descuento ?? 0)}%</span>,
      help: 'Descuento de compra de la marca. En la OC se sugiere como Desc. Marca (acumulativo con proveedor y SKU)',
    },
    {
      key: 'actions',
      header: '',
      render: (r: Marca) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDeleteTarget(r);
            setConfirmDelete(true);
          }}
          disabled={!can('catalogo', 'eliminar')}
          className="text-red-500 hover:text-red-700 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title={!can('catalogo', 'eliminar') ? 'No tenés permiso para realizar esta acción' : 'Eliminar marca'}
        >
          Eliminar
        </button>
      ),
      filterable: false,
    },
  ];

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Marcas</h1>
          <HelpTip
            tips={[
              'Administrá las marcas de los productos de cada proveedor',
              'Cada marca tiene un nombre único y puede estar asociada a un proveedor',
              'Importá marcas desde archivos CSV o XLSX con el botón 📤 Importar',
              'Descargá la plantilla para conocer el formato esperado',
            ]}
          />
          <p className="text-slate-500 text-sm mt-1">Gestión de marcas</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 gap-y-2">
          {/* Plantilla dropdown */}
          <div className="relative group">
            <button
              onClick={() => setPlantillaOpen(o => !o)}
              aria-expanded={plantillaOpen}
              aria-haspopup="true"
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium"
              title="Descargar plantilla para importar"
            >
              📥 Plantilla
            </button>
            {plantillaOpen && <div className="fixed inset-0 z-[15]" onClick={() => setPlantillaOpen(false)} />}
            <div className={`absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg transition-all z-20 min-w-[140px] ${plantillaOpen ? 'opacity-100 visible' : 'opacity-0 invisible group-hover:opacity-100 group-hover:visible'}`}>
              <button
                onClick={() => { downloadTemplate('csv'); setPlantillaOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 rounded-t-lg"
              >
                📄 Plantilla CSV
              </button>
              <button
                onClick={() => { downloadTemplate('xlsx'); setPlantillaOpen(false); }}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-50 rounded-b-lg"
              >
                📊 Plantilla XLSX
              </button>
            </div>
          </div>
          {/* Importar */}
          <label
            onClick={(e) => { if (!can('catalogo', 'crear')) e.preventDefault(); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${!can('catalogo', 'crear') ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer'}`}
            title={!can('catalogo', 'crear') ? 'No tenés permiso para realizar esta acción' : 'Importar marcas desde archivo CSV o XLSX'}
          >
            📤 Importar
            <input type="file" accept=".csv,.xlsx" onChange={handleImport} className="hidden" disabled={!can('catalogo', 'crear')} />
          </label>
          {/* Exportar XLSX */}
          <button
            onClick={handleXlsxExport}
            disabled={loading || filtered.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-brand-50 hover:border-brand-300 hover:text-brand-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title="Exportar a XLSX"
          >
            📊 Exportar XLSX
          </button>
          {/* Nueva */}
          <Button
            onClick={openCreate}
            disabled={!can('catalogo', 'crear')}
            title={!can('catalogo', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
          >
            + Nueva
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={openEdit}
        emptyMessage="No hay marcas cargadas"
        filters={filters}
        onFilterChange={(k, v) =>
          setFilters((f) =>
            v
              ? { ...f, [k]: v }
              : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)),
          )
        }
        title="Marcas"
      />

      {/* Create / Edit Modal */}
      <Modal
        open={open}
        cierre={cierreForm}
        title={editingNombre ? `Editar Marca: ${editingNombre}` : 'Nueva Marca'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Ej: Nike, Adidas..."
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            />
            {nombreError && <p className="text-red-600 text-sm">{nombreError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              Proveedor <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <select
              value={form.proveedorId}
              onChange={(e) => setForm({ ...form, proveedorId: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">— Sin proveedor —</option>
              {proveedores.map((p: ProveedorOption) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="marca-descuento">
              Desc. Marca % <span className="text-slate-400 font-normal">(descuento de compra: se aplica en cascada después del descuento del proveedor y antes del de SKU, no se suma)</span>
            </label>
            <input
              id="marca-descuento"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={form.descuento}
              onChange={(e) => setForm({ ...form, descuento: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="0"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={cierreForm.cerrar}
              className="px-4 py-2 text-sm"
            >
              Cancelar
            </button>
            <Button
              onClick={handleSave}
              loading={submitting}
              disabled={editingNombre ? !can('catalogo', 'editar') : !can('catalogo', 'crear')}
              title={(editingNombre ? !can('catalogo', 'editar') : !can('catalogo', 'crear')) ? 'No tenés permiso para realizar esta acción' : undefined}
            >
              {submitting ? 'Guardando...' : editingNombre ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Confirm
        open={confirmDelete}
        title="Eliminar Marca"
        message={`¿Estás seguro de eliminar "${deleteTarget?.nombre}"?`}
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
